import { useRouter } from 'found';
import { useCallback } from 'react';
/**
 * A very specific hook to create an onClick handler for a found Link that
 * navigates back to the provided `to` if it's the previous entry in the stack,
 * otherwise it moves forward to the location.
 */

function useNavigateBackHandler(to) {
  const routerContext = useRouter();
  const handleNavigate = useCallback(event => {
    if (!routerContext) return;
    const {
      match,
      router
    } = routerContext; // We currently have no history stacks within these views. If we do, we
    // need to keep track of a stack of location.index values (with keys),
    // and go back by the appropriate number.

    if (match.location.index) {
      const backPathname = router.createLocation(to).pathname;
      const removeTransitionHook = router.addTransitionHook(({
        pathname
      }) => {
        removeTransitionHook();

        if (pathname !== backPathname) {
          // If the navigation didn't go where we wanted, block the
          // transition and navigate the user to the correct location
          // instead.
          // FIXME: This double-rAF hack matches what we do in
          //  scroll-behavior, and in practice is a reliable-enough way to
          //  wait for navigation to settle down.
          requestAnimationFrame(() => requestAnimationFrame(() => router.push(to)));
          return false;
        }

        return undefined;
      }); // On a normal click, have the back arrow behave like a button that
      // does the backward navigation.

      event.preventDefault();
      router.go(-1);
    }
  }, [routerContext, to]);
  return useCallback(event => {
    // For anything other than a normal click, have the back arrow work like
    // a normal link.
    if (event.defaultPrevented || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return;
    }

    handleNavigate(event);
  }, [handleNavigate]);
}

export default useNavigateBackHandler;